import type { WizardFormData } from '../page';

interface Props {
  formData: WizardFormData;
  onChange: (updates: Partial<WizardFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string;
}

const goals = [
  {
    value: 'organizar',
    label: 'Organizar gastos',
    description: 'Saber para onde vai cada real do meu dinheiro',
    icon: 'ri-layout-grid-line',
    color: 'emerald',
  },
  {
    value: 'dividas',
    label: 'Sair das dívidas',
    description: 'Criar um plano para quitar minhas dívidas',
    icon: 'ri-money-dollar-circle-line',
    color: 'red',
  },
  {
    value: 'guardar',
    label: 'Guardar dinheiro',
    description: 'Construir uma reserva e poupar mensalmente',
    icon: 'ri-piggy-bank-line',
    color: 'amber',
  },
  {
    value: 'cartoes',
    label: 'Controlar cartões',
    description: 'Acompanhar faturas e evitar juros',
    icon: 'ri-bank-card-line',
    color: 'violet',
  },
  {
    value: 'visao',
    label: 'Visão geral',
    description: 'Ter uma visão completa da minha vida financeira',
    icon: 'ri-eye-line',
    color: 'sky',
  },
];

const colorMap: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-600',
  red: 'bg-red-100 text-red-500',
  amber: 'bg-amber-100 text-amber-600',
  violet: 'bg-violet-100 text-violet-600',
  sky: 'bg-sky-100 text-sky-600',
};

export const WizardStep3 = ({ formData, onChange, onNext, onBack, loading = false, error }: Props) => {
  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-700 text-gray-900 mb-1">Qual seu principal objetivo?</h2>
        <p className="text-gray-500 text-sm">Vamos personalizar sua experiência com base no que você quer alcançar</p>
      </div>

      {error && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {goals.map((goal) => (
          <button
            key={goal.value}
            onClick={() => onChange({ goal: goal.value })}
            disabled={loading}
            className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer disabled:opacity-60 ${
              formData.goal === goal.value
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            } ${goal.value === 'visao' ? 'col-span-2' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${colorMap[goal.color]}`}>
                <i className={`${goal.icon} text-lg`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-600 mb-0.5 ${formData.goal === goal.value ? 'text-emerald-700' : 'text-gray-900'}`}>
                  {goal.label}
                </p>
                <p className="text-xs text-gray-500 leading-snug">{goal.description}</p>
              </div>
              {formData.goal === goal.value && (
                <div className="w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full shrink-0 mt-0.5">
                  <i className="ri-check-line text-white text-xs" />
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="h-12 px-6 border border-gray-200 text-gray-700 font-600 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={!formData.goal || loading}
          className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <><i className="ri-loader-4-line animate-spin text-base" />Salvando...</>
          ) : (
            <>Continuar<i className="ri-arrow-right-line text-base" /></>
          )}
        </button>
      </div>
    </div>
  );
};
